import config from "config/config";

const RECAPTCHA_LOGIN_ACTION = "login";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
      };
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const onReady = () => {
      const enterprise = window.grecaptcha?.enterprise;
      if (!enterprise) {
        scriptLoadPromise = null;
        reject(new Error("reCAPTCHA Enterprise failed to initialize"));
        return;
      }

      enterprise.ready(() => resolve());
    };

    if (window.grecaptcha?.enterprise) {
      onReady();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src^="https://www.google.com/recaptcha/enterprise.js"]`
    );
    if (existingScript) {
      existingScript.addEventListener("load", onReady, { once: true });
      existingScript.addEventListener(
        "error",
        () => {
          scriptLoadPromise = null;
          reject(new Error("Failed to load reCAPTCHA script"));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error("Failed to load reCAPTCHA script"));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

export const isRecaptchaEnabled = (): boolean =>
  Boolean(config.recaptcha.siteKey?.trim());

export const executeRecaptcha = async (): Promise<string> => {
  const siteKey = config.recaptcha.siteKey?.trim();
  if (!siteKey) {
    throw new Error("Missing reCAPTCHA site key");
  }

  await loadRecaptchaScript(siteKey);

  const grecaptcha = window.grecaptcha?.enterprise;
  if (!grecaptcha) {
    throw new Error("reCAPTCHA Enterprise is not loaded");
  }

  return grecaptcha.execute(siteKey, { action: RECAPTCHA_LOGIN_ACTION });
};
